import { PlusIcon } from '@heroicons/react/24/outline';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { Textarea, TextField } from '@/shared/components/TextField';
import { Button } from '@/shared/components/Buttons';
import { Snackbar } from '@/shared/components/Snackbar';
import { Tooltip } from '@/shared/components/Tooltip';
import AddUserModal from '@/shared/layout/createLayout/CreateModal';
import StepperPageShell from '@/shared/layout/StepperPageShell';
import StepTabNav from '@/shared/components/StepTabNav';
import { OfferInfiniteDropdown } from '@/epics/subscriptionManagment/components/OfferInfiniteDropdown';
import { AppliedOfferCard } from '@/epics/subscriptionManagment/components/AppliedOfferCard';
import { ServiceConfigCard } from '@/epics/subscriptionManagment/components/ServiceConfigCard';
import { RemoveServiceDialog } from '@/epics/subscriptionManagment/components/RemoveServiceDialog';
import { EquipmentCategoryPriceRow } from '@/epics/subscriptionManagment/components/EquipmentCategoryPriceRow';
import { DiscountTierRow } from '@/epics/subscriptionManagment/components/DiscountTierRow';
import { PlanPreviewAccordion } from '@/epics/subscriptionManagment/components/PlanPreviewAccordion';
import { useMemo } from 'react';
import ErrorPage403 from '@/app/routes/errorPage403';
import Loader from '@/app/routes/Loader';
import { ToggleButton } from '@/shared/components/ToggleButton';
import { UpdateSubscriptionStatusDialog } from '../subscriptionList/UpdateSubscriptionStatusDialog';
import {
  useEditSubscription,
  type PlanDetailsStepProps,
  type ServicesStepProps,
  type EquipmentPricingStepProps,
  type DiscountLogicStepProps,
} from '@/epics/subscriptionManagment/hooks/useEditSubscription';
import {
  PERIOD_TYPE_OPTIONS,
  PERIOD_OPTIONS_MONTHLY,
  PERIOD_OPTIONS_YEARLY,
  STEP_TABS,
  EQUIPMENT_RANK_COUNT,
  MAX_PRICE_LENGTH,
  isPriceTooBig,
} from '@/epics/subscriptionManagment/hooks/useCreateSubscription';
import { useUserPrivileges } from '@shared/hooks/useUserPrivileges';
import { SUBSCRIPTION_ERROR_MESSAGES } from '../../errors/errorMessages';
import { SUBSCRIPTION_MANAGEMENT_LABELS } from '@/epics/subscriptionManagment/constants/translations/labels';

const EditSubscription = () => {
  const {
    isLoading,
    formData,
    setFormData,
    durationType,
    setDurationType,
    period,
    setPeriod,
    cycle,
    setCycle,
    cycleOptions,
    errors,
    setErrors,
    isSubmitting,
    isSnackbarOpen,
    snackbarError,
    isCancelModalOpen,
    handleSubmit,
    handleCancel,
    handleBackToEdit,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleCloseSnackbar,
    formatPriceWithSpaces,
    getBillingDescription,
    modifiedBy,
    modifiedDate,
    previewData,
    step,
    completedSteps,
    handleStepTabClick,
    isPreviewStep,
    status,
    activeClientCount,
    handleStatusChange,
    isStatusDialogOpen,
    handleCloseStatusDialog,
    handleConfirmStatusUpdate,
    isStatusSubmitting,
    showStatusSuccessSnackbar,
    statusSuccessMessage,
    handleCloseStatusSuccessSnackbar,
    showStatusErrorSnackbar,
    statusErrorMessage,
    handleCloseStatusErrorSnackbar,
    selectedOffer,
    offerName,
    offerDiscount,
    handleOfferChange,
    services,
    expandedServiceKey,
    serviceFieldErrors,
    servicesError,
    hasIncompleteService,
    removeDialogEntry,
    handleAddService,
    handleServiceTypeChange,
    handleServiceFieldChange,
    handleToggleExpandService,
    handleRequestRemoveService,
    handleCloseRemoveDialog,
    handleConfirmRemoveService,
    accessError,
    equipmentCategoryOptions,
    equipmentCategoriesLoading,
    equipmentPrices,
    equipmentPriceErrors,
    handleEquipmentPriceChange,
    discountTiers,
    discountTierErrors,
    handleDiscountTierChange,
  } = useEditSubscription();

  const { privileges, isLoading: privilegesLoading } = useUserPrivileges();
  const isMissingActivateDeactivatePrivilege =
    !privileges.subscriptionActivateDeactivate;
  const hasActiveClients = activeClientCount > 0;

  const EditSubscriptionHeader = () => {
    return (
      <div
        className={`flex items-center justify-between${isPreviewStep ? ' pb-3' : ''}`}
      >
        <p className="text-titleLarge text-neutral-10">
          {isPreviewStep ? 'Podgląd planu' : 'Edytuj plan'}
        </p>
        <div
          className={`flex items-center gap-2${isMissingActivateDeactivatePrivilege || hasActiveClients ? ' opacity-50' : ''}`}
        >
          <p className="text-[12px] text-on-surface-variant">Status:</p>
          <div
            className="flex min-w-[110px] items-center"
            data-testid="subscription-status-toggle"
          >
            <div className="leading-none">
              {isMissingActivateDeactivatePrivilege ? (
                <Tooltip
                  content={SUBSCRIPTION_ERROR_MESSAGES.NO_PERMISSION_TOOLTIP}
                  side="left"
                  forceShow
                  triggerWrapContent
                >
                  <ToggleButton
                    checked={status === 'active'}
                    onChange={() => {}}
                    label={status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                    size="XSmall"
                    disabled
                    data-testid="toggle-status"
                  />
                </Tooltip>
              ) : hasActiveClients ? (
                <Tooltip
                  content={SUBSCRIPTION_ERROR_MESSAGES.CANNOT_DEACTIVATE_ACTIVE_CLIENTS_TOOLTIP(
                    activeClientCount
                  )}
                  side="bottom"
                  forceShow
                  triggerWrapContent
                >
                  <ToggleButton
                    checked={status === 'active'}
                    onChange={() => {}}
                    label={status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                    size="XSmall"
                    disabled
                    data-testid="toggle-status"
                  />
                </Tooltip>
              ) : (
                <div
                  onClick={(e) => {
                    if (
                      isSubmitting ||
                      isLoading ||
                      isStatusSubmitting ||
                      isPreviewStep
                    ) {
                      return;
                    }
                    e.stopPropagation();
                    handleStatusChange();
                  }}
                  className={`flex min-w-[110px] items-center ${
                    isSubmitting ||
                    isLoading ||
                    isStatusSubmitting ||
                    isPreviewStep
                      ? 'cursor-not-allowed'
                      : 'cursor-pointer'
                  }`}
                >
                  <div className="leading-none pointer-events-none">
                    <ToggleButton
                      checked={status === 'active'}
                      onChange={() => {}}
                      label={status === 'active' ? 'Aktywny' : 'Nieaktywny'}
                      size="XSmall"
                      disabled={
                        isSubmitting ||
                        isLoading ||
                        isStatusSubmitting ||
                        isPreviewStep
                      }
                      data-testid="toggle-status"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const PlanDetailsStep = useMemo(
    () =>
      function PlanDetailsStep({
        formData,
        setFormData,
        durationType,
        setDurationType,
        period,
        setPeriod,
        cycle,
        setCycle,
        cycleOptions,
        errors,
        setErrors,
        formatPriceWithSpaces,
        getBillingDescription,
        isSubmitting,
        isPreviewStep,
        modifiedBy,
        modifiedDate,
        selectedOffer,
        offerName,
        offerDiscount,
        handleOfferChange,
      }: PlanDetailsStepProps) {
        return (
          <>
            <div className="flex flex-col gap-2 mt-[28px]">
              <p className="text-[18px] text-on-surface-variant">
                Szczegóły planu
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4 ">
                  <TextField
                    type="text"
                    inputMode="text"
                    label="Nazwa planu"
                    maxLength={200}
                    width={'50%'}
                    autoFocus={true}
                    value={formData.planName}
                    onChange={(e) => {
                      if (isSubmitting) {
                        return;
                      }
                      if (errors.planName) {
                        setErrors((prev) => ({
                          ...prev,
                          planName: '',
                        }));
                      }
                      const next = e.target.value.trimStart().slice(0, 200);
                      setFormData((prev) => ({
                        ...prev,
                        planName: next,
                      }));
                    }}
                    error={!!errors.planName}
                    errorMessage={errors.planName ?? ''}
                    disabled={isSubmitting}
                    readOnly={isPreviewStep}
                  />
                </div>
                <Textarea
                  error={!!errors.description}
                  errorMessage={errors.description ?? ''}
                  label="Opis"
                  maxLength={500}
                  fullWidth
                  rows={4}
                  value={formData.description}
                  onChange={(e) => {
                    if (isSubmitting) {
                      return;
                    }
                    if (errors.description) {
                      setErrors((prev) => ({
                        ...prev,
                        description: '',
                      }));
                    }
                    setFormData((prev) => ({
                      ...prev,
                      description:
                        e.target.value.trimStart().slice(0, 500) || '',
                    }));
                  }}
                  disabled={isSubmitting}
                  readOnly={isPreviewStep}
                />
                <div className="flex gap-4">
                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <TextField
                      fullWidth={true}
                      label="Zmodyfikowane przez"
                      maxLength={200}
                      value={modifiedBy}
                      disabled={true}
                    />
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: 0 }}>
                    <TextField
                      fullWidth={true}
                      label="Data modyfikacji"
                      maxLength={200}
                      value={modifiedDate}
                      disabled={true}
                    />
                  </div>
                  <div style={{ flex: '1 1 0', minWidth: 0 }} />
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Okres i rozliczenia
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <Dropdown
                    menuFullWidth
                    items={PERIOD_TYPE_OPTIONS}
                    error={!!errors.durationType}
                    errorMessage={errors.durationType ?? ''}
                    trigger={({ open, toggle, disabled, readOnly, error }) => (
                      <DropDownButton
                        label="Typ okresu"
                        value={durationType}
                        open={open}
                        toggle={toggle}
                        disabled={disabled}
                        readOnly={readOnly ?? false}
                        error={error}
                        hideChevron={readOnly ?? false}
                      />
                    )}
                    onItemClick={(id) => {
                      if (isSubmitting) {
                        return;
                      }
                      if (errors.durationType) {
                        setErrors((prev) => ({
                          ...prev,
                          durationType: '',
                        }));
                      }
                      setDurationType(id);
                    }}
                    disabled={isSubmitting}
                    readOnly={isPreviewStep}
                  />
                  <Dropdown
                    key={durationType || 'empty'}
                    menuFullWidth
                    menuMaxVisibleItems={5}
                    items={
                      durationType === 'Miesięczny'
                        ? PERIOD_OPTIONS_MONTHLY
                        : PERIOD_OPTIONS_YEARLY
                    }
                    error={!!errors.period}
                    errorMessage={errors.period ?? ''}
                    trigger={({ open, toggle, disabled, readOnly, error }) => (
                      <DropDownButton
                        label="Okres"
                        value={period}
                        open={open}
                        toggle={toggle}
                        disabled={disabled}
                        readOnly={readOnly ?? false}
                        error={error}
                        hideChevron={readOnly ?? false}
                      />
                    )}
                    onItemClick={(id) => {
                      if (isSubmitting) {
                        return;
                      }
                      if (errors.period) {
                        setErrors((prev) => ({
                          ...prev,
                          period: '',
                        }));
                      }
                      setPeriod(id);
                    }}
                    disabled={durationType.length === 0 || isSubmitting}
                    readOnly={isPreviewStep}
                  />
                  {cycleOptions.length === 1 ? (
                    <TextField
                      label="Cykl rozliczeniowy"
                      value={cycle}
                      fullWidth={true}
                      disabled={isSubmitting}
                    />
                  ) : (
                    <Dropdown
                      key={`${durationType}-${period}`}
                      menuFullWidth
                      items={cycleOptions}
                      error={!!errors.cycle}
                      errorMessage={errors.cycle ?? ''}
                      trigger={({
                        open,
                        toggle,
                        disabled,
                        readOnly,
                        error,
                      }) => (
                        <DropDownButton
                          label="Cykl rozliczeniowy"
                          value={cycle}
                          open={open}
                          toggle={toggle}
                          disabled={disabled}
                          readOnly={readOnly ?? false}
                          error={error}
                          hideChevron={readOnly ?? false}
                        />
                      )}
                      onItemClick={(id) => {
                        if (isSubmitting) {
                          return;
                        }
                        if (errors.cycle) {
                          setErrors((prev) => ({
                            ...prev,
                            cycle: '',
                          }));
                        }
                        setCycle(id);
                      }}
                      disabled={
                        durationType.length === 0 ||
                        period.length === 0 ||
                        isSubmitting
                      }
                      readOnly={isPreviewStep}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                Limity użytkowania
              </p>
              <div className="flex flex-col gap-8">
                <div className="flex gap-4">
                  <div className="flex flex-col gap-2 w-full">
                    <TextField
                      label="Liczba użytkowników"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={3}
                      fullWidth
                      value={formData.userCount}
                      onChange={(e) => {
                        if (isSubmitting) {
                          return;
                        }
                        if (errors.userCount) {
                          setErrors((prev) => ({
                            ...prev,
                            userCount: '',
                          }));
                        }
                        const digitsOnly = e.target.value.replace(/\D/g, '');

                        if (digitsOnly === '') {
                          setFormData((prev) => ({ ...prev, userCount: '' }));
                          return;
                        }

                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(500, Math.max(0, num));

                        setFormData((prev) => ({
                          ...prev,
                          userCount: String(clamped),
                        }));
                      }}
                      error={!!errors.userCount}
                      errorMessage={errors.userCount ?? ''}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                    />

                    {!errors.userCount && (
                      <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                        {
                          SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_MAX_USERS_HINT
                            .polish
                        }
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <TextField
                      label="Liczba placówek"
                      maxLength={2}
                      fullWidth
                      type="text"
                      inputMode="numeric"
                      value={formData.facilityCount}
                      onChange={(e) => {
                        if (isSubmitting) {
                          return;
                        }
                        if (errors.facilityCount) {
                          setErrors((prev) => ({
                            ...prev,
                            facilityCount: '',
                          }));
                        }
                        const digitsOnly = e.target.value.replace(/\D/g, '');

                        if (digitsOnly === '') {
                          setFormData((prev) => ({
                            ...prev,
                            facilityCount: '',
                          }));
                          return;
                        }

                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(10, Math.max(1, num));

                        setFormData((prev) => ({
                          ...prev,
                          facilityCount: String(clamped),
                        }));
                      }}
                      error={!!errors.facilityCount}
                      errorMessage={errors.facilityCount ?? ''}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                    />

                    {!errors.facilityCount && (
                      <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                        {
                          SUBSCRIPTION_MANAGEMENT_LABELS
                            .FIELD_MAX_FACILITIES_HINT.polish
                        }
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 w-full">
                    <TextField
                      label={
                        <>
                          Liczba dokumentów QMS{' '}
                          <span style={{ fontSize: 12 }}>
                            {
                              SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_OPTIONAL
                                .polish
                            }
                          </span>
                        </>
                      }
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={4}
                      fullWidth={true}
                      value={formData.qmsDocCount}
                      onChange={(e) => {
                        if (isSubmitting) {
                          return;
                        }
                        const digitsOnly = e.target.value.replace(/\D/g, '');
                        if (digitsOnly === '') {
                          setFormData((prev) => ({
                            ...prev,
                            qmsDocCount: '',
                          }));
                          return;
                        }
                        const num = parseInt(digitsOnly, 10);
                        const clamped = Math.min(2000, Math.max(0, num));
                        setFormData((prev) => ({
                          ...prev,
                          qmsDocCount: String(clamped),
                        }));
                      }}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">Cennik</p>
              <div className="flex flex-col gap-8">
                <div className="flex flex-col gap-2">
                  <div className="w-[32.7%]" data-field="subscription-price">
                    <TextField
                      fullWidth={true}
                      label="Cena"
                      maxLength={MAX_PRICE_LENGTH}
                      type="amount"
                      value={formatPriceWithSpaces(formData.price)}
                      onChange={(e) => {
                        if (isSubmitting) {
                          return;
                        }
                        const raw = e.target.value.replace(/\s/g, '');
                        if (isPriceTooBig(raw)) {
                          return;
                        }
                        if (errors.price) {
                          setErrors((prev) => ({
                            ...prev,
                            price: '',
                          }));
                        }
                        setFormData((prev) => ({
                          ...prev,
                          price: raw,
                        }));
                      }}
                      error={!!errors.price}
                      errorMessage={errors.price ?? ''}
                      disabled={isSubmitting}
                      readOnly={isPreviewStep}
                      trailingIcon={
                        formData.price ? (
                          <span className="text-bodyLarge text-neutral-10 align-middle ml-[2px] mt-[12px]">
                            zł
                          </span>
                        ) : null
                      }
                    />
                  </div>
                  <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                    {getBillingDescription(
                      formData.price,
                      cycle,
                      period,
                      formatPriceWithSpaces
                    )}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-b-2 border-[#DCDDE0]" />

            <div className="flex flex-col gap-2">
              <p className="text-[18px] text-on-surface-variant">
                {SUBSCRIPTION_MANAGEMENT_LABELS.SECTION_OFFER.polish}
              </p>
              <div className="flex flex-col">
                <div className="flex flex-col gap-2">
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-2 min-w-0">
                      <OfferInfiniteDropdown
                        value={selectedOffer}
                        onChange={handleOfferChange}
                        disabled={isPreviewStep || isSubmitting}
                        hideChevron={isPreviewStep}
                        label={
                          <>
                            {
                              SUBSCRIPTION_MANAGEMENT_LABELS
                                .FIELD_SELECT_DISCOUNT.polish
                            }{' '}
                            <span style={{ fontSize: 12 }}>
                              {
                                SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_OPTIONAL
                                  .polish
                              }
                            </span>
                          </>
                        }
                        placement="top-start"
                        displayName={offerName}
                      />
                    </div>
                  </div>
                  <p className="text-bodySmall text-on-surface-variant ml-[2px]">
                    {
                      SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DISCOUNT_HELPER
                        .polish
                    }
                  </p>
                </div>
                <div className="mt-8">
                  <AppliedOfferCard
                    planName={formData.planName}
                    price={formData.price}
                    cycle={cycle}
                    discount={selectedOffer ? offerDiscount : null}
                    formatPriceWithSpaces={formatPriceWithSpaces}
                  />
                </div>
              </div>
            </div>
          </>
        );
      },
    []
  );

  const ServicesStep = useMemo(
    () =>
      function ServicesStep({
        isSubmitting,
        isPreviewStep,
        services,
        expandedServiceKey,
        serviceFieldErrors,
        servicesError,
        hasIncompleteService,
        handleAddService,
        handleServiceTypeChange,
        handleServiceFieldChange,
        handleToggleExpandService,
        handleRequestRemoveService,
      }: ServicesStepProps) {
        return (
          <div className="flex flex-col gap-6">
            <div className="flex items-start justify-between gap-4">
              <div className="flex flex-col gap-1">
                <p className="text-[18px] text-on-surface-variant">
                  {SUBSCRIPTION_MANAGEMENT_LABELS.STEP_SERVICES.polish}
                </p>
                <p className="text-bodySmall text-on-surface-variant">
                  At least one service with a usage limit is required.
                </p>
              </div>
              <Tooltip
                content="Fill in all required fields to enable Add Service."
                forceShow={hasIncompleteService}
                useContentWidth
                contentMaxWidth={300}
                side="left"
                triggerWrapContent
              >
                <Button
                  type="button"
                  label={
                    SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_ADD_SERVICE.polish
                  }
                  buttonType="Square"
                  size="Small"
                  onClick={handleAddService}
                  disabled={
                    isSubmitting || isPreviewStep || hasIncompleteService
                  }
                  customBorderColor="var(--neutral-73)"
                  customBgColor="transparent"
                  customTextColor="var(--color-on-surface-variant)"
                  customFontWeight={500}
                  icon={<PlusIcon className="w-5 h-5" />}
                  showIcon
                  className="cursor-pointer hover:!bg-[#F0F0F0]"
                />
              </Tooltip>
            </div>

            {servicesError && (
              <p className="text-bodySmall text-error-40">{servicesError}</p>
            )}

            <div className="flex flex-col gap-3">
              {services.map((entry) => (
                <ServiceConfigCard
                  key={entry.key}
                  entry={entry}
                  expanded={expandedServiceKey === entry.key}
                  onToggleExpand={() => handleToggleExpandService(entry.key)}
                  onServiceChange={(serviceTypeId, serviceName) =>
                    handleServiceTypeChange(
                      entry.key,
                      serviceTypeId,
                      serviceName
                    )
                  }
                  onFieldChange={(field, value) =>
                    handleServiceFieldChange(entry.key, field, value)
                  }
                  onRemove={() => handleRequestRemoveService(entry.key)}
                  excludedServiceTypeIds={services
                    .filter((s) => s.key !== entry.key && s.serviceTypeId)
                    .map((s) => s.serviceTypeId)}
                  isOnlyService={services.length === 1}
                  errors={serviceFieldErrors[entry.key]}
                  disabled={isSubmitting || isPreviewStep}
                />
              ))}
            </div>
          </div>
        );
      },
    []
  );

  const EquipmentPricingStep = useMemo(
    () =>
      function EquipmentPricingStep({
        isSubmitting,
        isPreviewStep,
        equipmentCategoryOptions,
        equipmentCategoriesLoading,
        equipmentPrices,
        equipmentPriceErrors,
        handleEquipmentPriceChange,
      }: EquipmentPricingStepProps) {
        return (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <p className="text-[18px] text-on-surface-variant">
                {SUBSCRIPTION_MANAGEMENT_LABELS.STEP_EQUIPMENT_PRICING.polish}
              </p>
              <p className="text-bodySmall text-on-surface-variant">
                Update the base maintenance price for each equipment category.
                Discounts are auto-assigned and can be adjusted per category.
              </p>
            </div>

            {equipmentCategoriesLoading ? (
              <Loader />
            ) : (
              <div className="flex flex-col gap-5">
                {equipmentCategoryOptions.map((category) => (
                  <EquipmentCategoryPriceRow
                    key={category.id}
                    categoryId={category.id}
                    categoryLabel={category.label}
                    basePrice={equipmentPrices[category.id] ?? ''}
                    onBasePriceChange={(value) =>
                      handleEquipmentPriceChange(category.id, value)
                    }
                    error={equipmentPriceErrors[category.id]}
                    disabled={isSubmitting || isPreviewStep}
                  />
                ))}
              </div>
            )}
          </div>
        );
      },
    []
  );

  const DiscountLogicStep = useMemo(
    () =>
      function DiscountLogicStep({
        isSubmitting,
        isPreviewStep,
        discountTiers,
        discountTierErrors,
        handleDiscountTierChange,
      }: DiscountLogicStepProps) {
        return (
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <p className="text-[18px] text-on-surface-variant">
                {SUBSCRIPTION_MANAGEMENT_LABELS.STEP_DISCOUNT_LOGIC.polish}
              </p>
              <p className="text-bodySmall text-on-surface-variant">
                {
                  SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_DISCOUNT_TIERS_HELPER
                    .polish
                }
              </p>
            </div>

            <div className="flex flex-col gap-5">
              {Array.from(
                { length: EQUIPMENT_RANK_COUNT },
                (_, i) => i + 1
              ).map((rank) => (
                <DiscountTierRow
                  key={rank}
                  rank={rank}
                  discountPercentage={discountTiers[rank] ?? ''}
                  onDiscountPercentageChange={(value) =>
                    handleDiscountTierChange(rank, value)
                  }
                  error={discountTierErrors[rank]}
                  disabled={isSubmitting || isPreviewStep}
                />
              ))}
            </div>
          </div>
        );
      },
    []
  );

  if (!privilegesLoading && !privileges.subscriptionEdit) {
    return <ErrorPage403 />;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center bg-white h-screen w-full">
        <Loader />
      </div>
    );
  }

  if (accessError) {
    return <ErrorPage403 />;
  }

  const submitLabel = isSubmitting
    ? 'Saving Changes'
    : isPreviewStep
      ? 'Save Changes'
      : step < 4
        ? 'Next'
        : 'Przejdź do podglądu';

  return (
    <>
      <StepperPageShell
        as="form"
        onSubmit={handleSubmit}
        activeStep={isPreviewStep ? 'preview' : step}
        title={<EditSubscriptionHeader />}
        stepper={
          !isPreviewStep && (
            <StepTabNav
              steps={STEP_TABS}
              currentStep={step}
              maxStep={4}
              completedSteps={completedSteps}
              onStepClick={handleStepTabClick}
            />
          )
        }
        footer={
          <>
            <Button
              label={SUBSCRIPTION_MANAGEMENT_LABELS.BUTTON_CANCEL.polish}
              buttonType="Square"
              type="button"
              onClick={handleCancel}
              disabled={isSubmitting}
              className="!bg-transparent !h-[40px] cursor-pointer !text-labelLarge !text-on-surface-variant hover:!border-neutral-73 hover:!bg-on-surface-variant/[0.08] !rounded-[8px]"
            />
            <div className="flex items-center gap-4">
              {isPreviewStep ? (
                <Button
                  label={
                    SUBSCRIPTION_MANAGEMENT_LABELS.BUTTON_BACK_TO_EDIT.polish
                  }
                  buttonType="Square"
                  type="button"
                  onClick={handleBackToEdit}
                  disabled={isSubmitting}
                  className="!bg-transparent !h-[40px] cursor-pointer !text-labelLarge !text-on-surface-variant border border-neutral-73 hover:!bg-on-surface-variant/[0.08] !rounded-[8px]"
                />
              ) : (
                step > 1 && (
                  <Button
                    label="Back"
                    buttonType="Square"
                    type="button"
                    onClick={() => handleStepTabClick(step - 1)}
                    disabled={isSubmitting}
                    className="!bg-transparent !h-[40px] cursor-pointer !text-labelLarge !text-on-surface-variant border border-neutral-73 hover:!bg-on-surface-variant/[0.08] !rounded-[8px]"
                  />
                )
              )}
              <Button
                label={submitLabel}
                type="submit"
                disabled={isSubmitting}
                className="!h-[40px] text-labelLarge cursor-pointer !rounded-[8px]"
              />
            </div>
          </>
        }
      >
        {/* All 4 steps stay mounted; only visibility toggles, to avoid a
            remount/flicker on step switch. Preview replaces them with a
            read-only accordion summary instead. */}
        <div
          className={`flex flex-col gap-8 ${step === 1 && !isPreviewStep ? '' : 'hidden'}`}
        >
          <PlanDetailsStep
            formData={formData}
            setFormData={setFormData}
            durationType={durationType}
            setDurationType={setDurationType}
            period={period}
            setPeriod={setPeriod}
            cycle={cycle}
            setCycle={setCycle}
            cycleOptions={cycleOptions}
            errors={errors}
            setErrors={setErrors}
            formatPriceWithSpaces={formatPriceWithSpaces}
            getBillingDescription={getBillingDescription}
            isSubmitting={isSubmitting}
            isPreviewStep={isPreviewStep}
            modifiedBy={modifiedBy}
            modifiedDate={modifiedDate}
            selectedOffer={selectedOffer}
            offerName={offerName}
            offerDiscount={offerDiscount}
            handleOfferChange={handleOfferChange}
          />
        </div>
        <div
          className={`mt-[28px] ${step === 2 && !isPreviewStep ? '' : 'hidden'}`}
        >
          <ServicesStep
            isSubmitting={isSubmitting}
            isPreviewStep={isPreviewStep}
            services={services}
            expandedServiceKey={expandedServiceKey}
            serviceFieldErrors={serviceFieldErrors}
            servicesError={servicesError}
            hasIncompleteService={hasIncompleteService}
            handleAddService={handleAddService}
            handleServiceTypeChange={handleServiceTypeChange}
            handleServiceFieldChange={handleServiceFieldChange}
            handleToggleExpandService={handleToggleExpandService}
            handleRequestRemoveService={handleRequestRemoveService}
          />
        </div>
        <div
          className={`mt-[28px] ${step === 3 && !isPreviewStep ? '' : 'hidden'}`}
        >
          <EquipmentPricingStep
            isSubmitting={isSubmitting}
            isPreviewStep={isPreviewStep}
            equipmentCategoryOptions={equipmentCategoryOptions}
            equipmentCategoriesLoading={equipmentCategoriesLoading}
            equipmentPrices={equipmentPrices}
            equipmentPriceErrors={equipmentPriceErrors}
            handleEquipmentPriceChange={handleEquipmentPriceChange}
          />
        </div>
        <div
          className={`mt-[28px] ${step === 4 && !isPreviewStep ? '' : 'hidden'}`}
        >
          <DiscountLogicStep
            isSubmitting={isSubmitting}
            isPreviewStep={isPreviewStep}
            discountTiers={discountTiers}
            discountTierErrors={discountTierErrors}
            handleDiscountTierChange={handleDiscountTierChange}
          />
        </div>

        {isPreviewStep && (
          <div className="mt-[28px]">
            <PlanPreviewAccordion data={previewData} />
          </div>
        )}
      </StepperPageShell>

      <Snackbar
        open={isSnackbarOpen}
        message={snackbarError}
        variant="error"
        onClose={handleCloseSnackbar}
        autoHideDuration={5000}
        position="top-center"
        showCloseButton={true}
      />

      <AddUserModal
        open={isCancelModalOpen}
        onClose={handleCloseCancelModal}
        onConfirmCancel={handleConfirmCancel}
      />

      {/* Status Update Dialog */}
      <UpdateSubscriptionStatusDialog
        open={isStatusDialogOpen}
        onClose={handleCloseStatusDialog}
        planName={formData.planName}
        isActive={status === 'active'}
        onConfirm={handleConfirmStatusUpdate}
        isSubmitting={isStatusSubmitting}
      />

      <RemoveServiceDialog
        open={removeDialogEntry !== null}
        onClose={handleCloseRemoveDialog}
        serviceName={removeDialogEntry?.serviceName || 'this service'}
        onConfirm={handleConfirmRemoveService}
      />

      {/* Status Update Success Snackbar */}
      <Snackbar
        open={showStatusSuccessSnackbar}
        message={statusSuccessMessage}
        variant="success"
        onClose={handleCloseStatusSuccessSnackbar}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />

      {/* Status Update Error Snackbar */}
      <Snackbar
        open={showStatusErrorSnackbar}
        message={statusErrorMessage}
        variant="error"
        onClose={handleCloseStatusErrorSnackbar}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton={true}
      />
    </>
  );
};

export default EditSubscription;
